import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { editContractVisitorAcGroupManageAsync } from "src/api/access/ac-group-api";
import { Ac2AccessGroupModel } from "src/api/access/ac-group-types";
import { getAccessGroupListAsync } from "src/api/access/productac-api";
import { useApiOperation } from "src/api/hooks";
import { BaseButton } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import SelectAcGroupModal from "src/pages/product/forms/accessLevelForm/modals/SelectAcGroupModal";
import SpaceBaseTable from "src/pages/product/forms/commonSpaceForm/components/SpaceBaseTable";
import { useModal } from "src/recoil/modalState/hook";
import { isMobilePath } from "src/utils";
import useGetPermissionAcGroup from "../hooks/useGetPermissionAcGroup";
import useVisitor from "../hooks/useVisitor";
import { acGroupColumns, editTypeDeleteCol } from "./acColumns";
import AllAccessDevicesModal from "./AllAccessDevicesModal";

/**
 * 방문자 출입그룹 수정시에만 사용하는 컴포넌트
 */
const VisitorEditAccessGroup = () => {
  const { isAuthority } = usePartnerAuthority();
  const { setBaseModal, setAbstractModalZ1 } = useModal();

  const { contractApplyNumber, visitApplyNumber } = useParams();
  const location = useLocation();
  const { visitorGroup } = useVisitor({
    contractApplyNumber: contractApplyNumber,
    visitApplyNumber: visitApplyNumber,
  });

  const [showSelectModal, setShowSelectModal] = useState(false);

  // 출입그룹 목록
  const [accessGroupList, setAccessGroupList] = useState<Ac2AccessGroupModel[]>([]);

  const newColumns = useMemo(() => {
    /* 방문상태가 종료,삭제일때 수정불가 */
    if (visitorGroup?.visitStatus !== "VISIT_CLOSE" && visitorGroup?.visitStatus !== "VISIT_DELETE") {
      // 대기 상태일때 삭제 가능
      if (visitorGroup?.approvalStatus === "APPROVAL_PENDING" && isAuthority("d")) {
        return [...acGroupColumns, ...editTypeDeleteCol];
      }
    }

    return acGroupColumns;
  }, [visitorGroup?.approvalStatus, isAuthority, visitorGroup?.visitStatus]);

  // 계약 관리 출입 설정 상세 조회(커스텀 권한 조회)
  const { visitorDefaultAccessGroupYn } = useGetPermissionAcGroup(Number(visitorGroup?.contractManageId));

  //출입그룹 목록 조회
  const { executeAsync: getAccessList } = useApiOperation(getAccessGroupListAsync);

  const { executeAsync: editContractVisitorAcGroup } = useApiOperation(editContractVisitorAcGroupManageAsync);

  const fetchContractAccess = useCallback(async () => {
    if (visitorDefaultAccessGroupYn === false) {
      const { data } = await getAccessList({
        contractVisitorGroupId: visitorGroup?.contractVisitorGroupId?.toString(),
        visitorAccessYn: true,
      });
      setAccessGroupList(data?.data.content || []);
    }
  }, [getAccessList, visitorDefaultAccessGroupYn, visitorGroup?.contractVisitorGroupId]);

  // 출입그룹 추가 핸들러
  const handleAddAccessGroups = useCallback(
    async (selectedGroups: Ac2AccessGroupModel[]) => {
      // 이미 존재하는 그룹은 id는 제외하고 보내기
      const fillteredAccessGroupList = selectedGroups.filter((group) => !accessGroupList.some((g) => g.id === group.id));

      await editContractVisitorAcGroup({
        contractVisitorGroupId: visitorGroup?.contractVisitorGroupId?.toString() || "",
        accessGroupAddIds: fillteredAccessGroupList.map((group) => group.id),
      });

      //출입그룹 리프래시
      await fetchContractAccess();
      setShowSelectModal(false);
    },
    [accessGroupList, editContractVisitorAcGroup, fetchContractAccess, visitorGroup?.contractVisitorGroupId],
  );

  useEffect(() => {
    // 출입 그룹 조회
    fetchContractAccess();
  }, [fetchContractAccess]);

  // 출입그룹 삭제 핸들러
  const handleDeleteAccessGroup = useCallback(
    async (deleteId: number) => {
      // 이미 존재하는 그룹은 id는 제외하고 보내기

      setBaseModal({
        isOpen: true,
        title: "해당 출입그룹을 삭제합니다",
        children: "삭제한 즉시 해당 출입그룹에 액세스 할 수 없습니다",
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: async () => {
          await editContractVisitorAcGroup({
            contractVisitorGroupId: visitorGroup?.contractVisitorGroupId?.toString() || "",
            accessGroupDeleteIds: [Number(deleteId)],
          });

          //출입그룹 리프래시
          await fetchContractAccess();
          setBaseModal({
            isOpen: false,
          });
        },
      });
    },
    [editContractVisitorAcGroup, fetchContractAccess, visitorGroup?.contractVisitorGroupId],
  );

  return (
    <article
      className={`contents-container__wrap-article mobile-access-table ${
        isMobilePath(location.pathname) && visitorGroup?.visitStatus !== "VISIT_CLOSE" && visitorGroup?.visitStatus !== "VISIT_DELETE"
          ? "pb140"
          : isMobilePath(location.pathname)
          ? "pb90"
          : ""
      }`}
    >
      <div className="flex-center-between w-100">
        <BaseSectionTitle
          title={"출입그룹 관리"}
          className=""
          rightArea={
            <div className="add-access-btn-wrap">
              <BaseButton
                title="장치목록 한번에 보기"
                className="color-white"
                onClick={() =>
                  setAbstractModalZ1({
                    size: "large",
                    isOpen: true,
                    children: <AllAccessDevicesModal accessGroupIds={accessGroupList.map((group) => group.id).join(",")} />,
                  })
                }
              />
              {/* 종료,삭제일때 수정불가 */}
              {visitorGroup?.visitStatus !== "VISIT_CLOSE" && visitorGroup?.visitStatus !== "VISIT_DELETE" && (
                <>
                  {isAuthority("w") && visitorGroup?.approvalStatus === "APPROVAL_PENDING" && (
                    // 대기 상태일때 추가 가능
                    <BaseButton
                      title="+ 출입그룹 추가"
                      onClick={() => {
                        setShowSelectModal(true);
                      }}
                    />
                  )}
                </>
              )}
            </div>
          }
        />
        <div className="contents-container__grid-contents flex-center"></div>
        {showSelectModal && visitorGroup?.contractManageId && (
          <SelectAcGroupModal
            setShowModal={setShowSelectModal}
            onSelectedAccessGroups={handleAddAccessGroups}
            existingAccess={accessGroupList}
            contractManageId={visitorGroup?.contractManageId?.toString()}
            visitorAccessYn={true}
          />
        )}
      </div>
      <section>
        <SpaceBaseTable columns={newColumns} data={accessGroupList} handleDeleteAccessGroup={handleDeleteAccessGroup} baseTableTdClassName="h50" />
      </section>
    </article>
  );
};

export default VisitorEditAccessGroup;
